import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { getAuditableElements as getAuditableElementsServ } from 'solaris-lens-common-lib/src/services/auditableElements';

import { AuditableElements, ApiError } from './types';

type AuditableElementsState = AuditableElements;

const getErrorObject = (error: ApiError) => {
  const code = error?.code ?? 500;
  const description = error?.description ?? 'Unexpected error';
  const technicalMessage = error?.technicalMessage ?? 'Unexpected error';

  return {
    code,
    description,
    technicalMessage
  };
};

export const fetchAuditableElements = createAsyncThunk(
  'auditableElements/get',
  async (
    { applicationName }: { applicationName: string },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as { auth: { accessToken: string } };
    const token = state.auth.accessToken;
    try {
      const response = await getAuditableElementsServ(token, applicationName);
      return response;
    } catch (err) {
      return rejectWithValue(getErrorObject(err as ApiError));
    }
  }
);

const initialState: AuditableElementsState = {
  status: { code: 0, message: '', description: '' },
  error: { description: '', code: 0 },
  authorizations: {
    permissions: {}
  },
  id: '',
  applicationName: '',
  bundleAppId: '',
  bundleAppName: ''
};

export const auditableElementsSlice = createSlice({
  name: 'auditableElements',
  initialState,
  reducers: {
    getAuditableElements: (state, action: PayloadAction<AuditableElements>) => {
      return {
        ...state,
        ...action.payload
      };
    },
    clearAuditableElements: () => initialState
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAuditableElements.pending, state => {
        state.status = { code: 1, message: 'loading', description: '' };
      })
      .addCase(fetchAuditableElements.fulfilled, (state, action) => {
        state.status = {
          ...action.payload.status
        };
        Object.assign(state, action.payload);
      })
      .addCase(fetchAuditableElements.rejected, (state, action) => {
        const payload = action.payload as ApiError;
        state.status = { code: 401, message: 'failed', description: '' };
        state.error = {
          description: payload.description,
          code: payload.code
        };
      });
  }
});

export const { getAuditableElements, clearAuditableElements } =
  auditableElementsSlice.actions;

export default auditableElementsSlice.reducer;
